module GraphQL.Query where

import Common

import Control.Monad.Except (ExceptT(..), except, runExceptT)
import Data.Argonaut (class DecodeJson, class EncodeJson, decodeJson, fromObject, jsonNull)
import Data.Array ((:))
import Data.Symbol (class IsSymbol, reflectSymbol)
import Foreign.Object (Object)
import Foreign.Object as O
import GraphQL (fileQuery, query)
import GraphQL.Join (class GraphQL, class JoinableQuery, getDecode)
import GraphQL.Shape (class HasShape, QueryArg(..), QueryField(..), QueryKey, QueryShape, shapeOf, shapeToString)
import GraphQL.Type (class HasQType, DyNamedType(..), NamedType, QType(..), RenamedType(..), gqlType, typeToString)
import Prim.Row as Row
import Prim.RowList as RL
import Record as Rec
import Type.Prelude (Proxy(..))
import Web.File.Blob (Blob)
import Web.File.File (File)
import Web.File.File as F
import Web.XHR.FormData (FileName(..))

-- | An encapsulation of a GraphQL query, its variables, and an associated
-- | decode function which should return a result of type `a` when executed
-- | with `runGraphQL`.
-- |
-- | Instances of this should be constructed with the `buildQuery` function.
-- | The query itself will be generated based on the inferred return type.
-- |
-- | This type also implements `Functor` which allows the `map` function to be
-- | used to apply further processing to the result of the query by wrapping
-- | the decode function. For fallible processing see `queryThen`.
data Query a = Query (QueryRec a)

-- | Similar to `Query`, but for mutations instead. Construct with
-- | `buildMutation`.
-- |
-- | For now this is a synonym of `Query`, but that may change in the future.
type Mutation a = Query a

-- | Construct a GraphQL query of the given name based on the structure of
-- | `vars` and `ret`.
-- |
-- | `vars` and `ret` are determined by type inference in the context where
-- | this function is called and determine the behavior of the query generation
-- | and decoding.
-- |
-- | For queries that simply return a basic object or array, this should just
-- | work so long as the fields on the `ret` object match the fields on the
-- | GraphQL query by name.
-- |
-- | For more complex things like paginated queries, you must ensure that this
-- | function is either returning a `Page`, or pass its returned query through
-- | the `unpaged` function. See `API.Pagination` for more info.
buildQuery :: forall ret vars. ToVars vars => QueryResult ret => String -> vars -> Query ret
buildQuery name vars = Query $ buildQuery_ false name vars

-- | Version of `buildQuery` for mutations
buildMutation :: forall ret vars. ToVars vars => QueryResult ret => String -> vars -> Mutation ret
buildMutation name vars = Query $ buildQuery_ true name vars

-- | Attach a name to a query to be used in the `query <name>(<args>)` portion of the GraphQL query
-- |
-- | Will be discarded if the query is used in a compound query
nameQuery :: forall ret. String -> Query ret -> Query ret
nameQuery name (Query q) = Query $ q { name = name }

buildQuery_ :: forall ret vars. ToVars vars => QueryResult ret => Boolean -> String -> vars -> QueryRec ret
buildQuery_ mutation name vars' = fromBaseQuery query vars decode
  where
    -- Encode vars into JSON
    vars = encodeVars vars'
    -- Collect any file uploads
    files = varFiles vars'
    -- Generate the argument list for the query by combining the passed
    -- variables with whatever additional arguments the `QueryResult`
    -- specifies.
    args = toVars vars' <> (map (\(Tuple v _) -> v) $ queryResultArgs (Proxy :: Proxy ret))
    callArgs = map (\(QueryVar n _) -> QueryArg n n) args
    -- Generate a `QueryShape` matching the `queryResultShape` for whatever
    -- return type this function was inferred to have.
    shape = [QCall name callArgs $ queryResultShape (Proxy :: Proxy ret)]
    -- Build a `QueryResultContext` and create the decode function from it.
    decode = queryResultDecode { query, qstr: queryToString query, vars: fromObject vars }
    -- Finally, build our `BaseQuery` to pass to `fromBaseQuery`
    query = { name, args, files, shape, mutation }


instance qgql :: GraphQL (Query a) a where
    runGraphQL q'@(Query q@{ vars, files: [] }) = runExceptT do
        ret <- ExceptT $ query (queryToString q) vars
        except $ getDecode q' ret
    runGraphQL q'@(Query q@{ vars, files: [Tuple fkey (FileUpload fu)] }) = runExceptT do
        ret <- ExceptT $ fileQuery (queryToString q) vars fkey fu.name fu.file
        except $ getDecode q' ret
    runGraphQL _ = pure $ Left $
        StringError "Multiple file uploads are not currently supported"

instance joinGenQ :: JoinableQuery (Query a) a where
    getQuery (Query q@{ vars }) = { query: queryToString q, variables: fromObject vars }
    getDecode (Query { keyName, decode }) json = do
       inner <- wrapJsonError' json (decodeJObject json >>= (_ .: "data") >>= (_ .: keyName))
       decode inner

-- Allow mapping the result type of a query
instance queryFunctor :: Functor Query where
    map f (Query q@{ decode }) = Query $ q { decode = map f <<< decode }

-- | Monadic bind on the result type of a query for additional processing that may fail
-- |
-- | For infallible operations use `map` instead
queryThen :: forall a b. Query a -> (a -> Either ErrorWrapper b) -> Query b
queryThen (Query q@{ decode }) f = Query $ q { decode = \r -> decode r >>= f }


type QueryRec a = BaseQuery
    ( decode :: Json -> Either ErrorWrapper a
    , vars :: Object Json
    -- JSON key to decode
    , keyName :: String
    )

-- | Base record for queries
type BaseQuery r =
    { name :: String
    , args :: Array QueryVar
    , files :: Array (Tuple String FileUpload)
    , shape :: QueryShape
    , mutation :: Boolean
    | r
    }

fromBaseQuery :: forall a. BaseQuery () -> Object Json -> (Json -> Either ErrorWrapper a) -> QueryRec a
fromBaseQuery bq vars decode = Rec.merge { vars, decode, keyName: bq.name } bq

queryToString :: forall r. BaseQuery r -> String
queryToString { name, args, shape, mutation } =
    qormut <> " " <> name <> varstr <> " " <> shapestr
  where
    qormut | mutation = "mutation"
    qormut = "query"

    varstr | null args = ""
    varstr = "(" <> (intercalate ", " $ map varToString args) <> ")"

    shapestr | null shape = ""
    shapestr = shapeToString shape

-- | Defines a GraphQL variable/argument definition
data QueryVar = QueryVar QueryKey QType

varToString :: QueryVar -> String
varToString (QueryVar name qtype) = "$" <> name <> ": " <> typeToString qtype


-- | Class for types which get can be used as the result of a query generated
-- | by `buildQuery`
-- |
-- | There is a basic implementation of this for all records and arrays which
-- | implement `DecodeJson` and `HasShape` which can be used in most cases, as
-- | well as an implementation for the `Page` type for paginated queries.
-- |
-- | Implementations need only define a decode function and a function to
-- | determine the shape the query should use.
class QueryResult a where
    -- | Decodes the JSON returned by GraphQL into the result type
    queryResultDecode :: QueryResultContext -> Json -> Either ErrorWrapper a
    -- | Determine the `QueryShape` to use for this result type
    queryResultShape :: Proxy a -> QueryShape
    -- | Allows the `QueryResult` to inject extra arguments in the query
    -- | (useful for pagination)
    queryResultArgs :: Proxy a -> Array (Tuple QueryVar Json)

-- | Extra context passed to the `queryResultDecode` function
type QueryResultContext =
    { query :: BaseQuery ()
    , qstr :: String
    , vars :: Json
    }

instance jsonQR :: (DecodeJson (Record r), HasShape (Record r)) => QueryResult (Record r) where
    -- For basic record types which implement `DecodeJson`, fall back on the
    -- argonaut `decodeJson` implementation
    queryResultDecode _ json = wrapJsonError' json $ decodeJson json
    -- Here we just use the result type's shape as-is
    queryResultShape p = shapeOf p
    queryResultArgs _ = []

-- The case for arrays is identical to the case for records. Simply fall back
-- on the inner type's instances of `DecodeJson` and `HasShape` directly 
instance arrayQR :: (DecodeJson (Array item), HasShape (Array item)) => QueryResult (Array item) where
    -- Again fall back on the `DecodeJson` instance
    queryResultDecode _ json = wrapJsonError' json $ decodeJson json
    -- Here we just use the result type's shape as-is
    queryResultShape p = shapeOf p
    queryResultArgs _ = []

-- Unit simply ignores any return from the query. Only works for scalar results
instance unitQR :: QueryResult Unit where
    queryResultDecode _ _ = Right unit
    queryResultShape _ = []
    queryResultArgs _ = []

-- | Class for types which can be encoded as variables/arguments to a GraphQL query.
-- |
-- | There is a built-in implementation of this for records whose fields all
-- | implement `EncodeJson`.
class ToVars a where
    -- | Generate an array of `QueryVar`s for the query's argument list
    toVars :: a -> Array QueryVar
    -- | Encode the variables into a JSON object
    encodeVars :: a -> Object Json
    -- | Get a list of files in the variable set paired with dot-separated
    -- | paths to their fields
    varFiles :: a -> Array (Tuple String FileUpload)

instance rlTVars ::
    ( RLToVars row list
    , RLTVFiles row list
    , RL.RowToList row list
    ) => ToVars (Record row) where
        toVars rec = rlToVars (Proxy :: Proxy list) rec
        encodeVars = rlEncodeVars (Proxy :: Proxy list)
        varFiles = rlFiles "" (Proxy :: Proxy list)

class RLToVars (row :: Row Type) (list :: RL.RowList Type) where
    rlToVars :: Proxy list -> Record row -> Array QueryVar
    rlEncodeVars :: Proxy list -> Record row -> Object Json

-- Special case for `DyNamedType`
instance rltvDyTyCons ::
    ( RLToVars rtail tail
    , IsSymbol field
    , EncodeJson inner
    , Row.Cons field (DyNamedType inner) rtail row
    , Row.Lacks field rtail
    ) => RLToVars row (RL.Cons field (DyNamedType inner) tail) where
        rlToVars _ rec = thisVar : rlToVars (Proxy :: Proxy tail) (Rec.delete fproxy rec)
          where
            fproxy = Proxy :: Proxy field
            name = reflectSymbol fproxy
            (DyNamedType ty _) = Rec.get fproxy rec
            thisVar = QueryVar name $ QType $ ty
        rlEncodeVars _ r = O.insert name val $
            rlEncodeVars (Proxy :: Proxy tail) $ Rec.delete field r
          where
            val = encodeJson $ Rec.get field r
            name = reflectSymbol field
            field = Proxy :: Proxy field
else
instance rltvCons ::
    ( RLToVars rtail tail
    , IsSymbol field
    , HasQType value
    , EncodeJson value
    , Row.Cons field value rtail row
    , Row.Lacks field rtail
    ) => RLToVars row (RL.Cons field value tail) where
        rlToVars _ rec = thisVar : rlToVars (Proxy :: Proxy tail) (Rec.delete fproxy rec)
          where
            thisVar = QueryVar name ty
            ty = gqlType (Proxy :: Proxy value)
            name = reflectSymbol fproxy
            fproxy = Proxy :: Proxy field
        rlEncodeVars _ r = O.insert name val $
            rlEncodeVars (Proxy :: Proxy tail) $ Rec.delete field r
          where
            val = encodeJson $ Rec.get field r
            name = reflectSymbol field
            field = Proxy :: Proxy field
        
instance rltvNil :: RLToVars row RL.Nil where
    rlToVars _ _ = []
    rlEncodeVars _ _ = O.empty


-- | Wrapper for doing file uploads in a GraphQL query.
-- |
-- | Simply pass this as a variable
data FileUpload = FileUpload { name :: Maybe FileName, file :: Blob }

-- File uploads live on Query, but getQuery only returns the JSON operation.
-- This keeps the Blob available so a batched GraphQL request can build a multipart upload.
-- @todo Move uploads into GQLQuery so callers do not pass query data and upload data separately.
type QueryUpload =
    { path :: String
    , name :: Maybe FileName
    , file :: Blob
    }

-- See above comment on the `QueryUpload` type.
queryUploads :: forall a. Query a -> Array QueryUpload
queryUploads (Query { files }) = map toUpload files
  where
    toUpload (Tuple path (FileUpload { name, file })) = { path, name, file }

-- | Construct a `FileUpload` from an un-named blob
-- |
-- | The file will be uploaded with no filename attached
blobUpload :: Blob -> FileUpload
blobUpload file = FileUpload { name: Nothing, file }

-- | Construct a named `FileUpload` from a `File` object
-- |
-- | Will be uploaded with whatever name is attached to the file
fileUpload :: File -> FileUpload
fileUpload file = FileUpload { name: Just $ FileName $ F.name file, file: F.toBlob file }

-- | Change the filename used to upload a file
renameUpload :: String -> FileUpload -> FileUpload
renameUpload name (FileUpload fu) = FileUpload $ fu { name = Just $ FileName name }

-- Dummy instance to pass null for this variable. Actual file will be attached via http multipart.
instance fuEnc :: EncodeJson FileUpload where
    encodeJson _ = jsonNull

class RLTVFiles (row :: Row Type) (list :: RL.RowList Type) where
    rlFiles :: String -> Proxy list -> Record row -> Array (Tuple String FileUpload)
-- If the current value is a `FileUpload` add it to the list
instance rltvfConsFU :: 
    ( RLTVFiles row tail
    , IsSymbol field
    , Row.Cons field FileUpload rtail row
    ) => RLTVFiles row (RL.Cons field FileUpload tail) where
        rlFiles pfx _ rec = thisFile : rlFiles pfx (Proxy :: Proxy tail) rec
          where
            thisFile = Tuple (pfx <> reflectSymbol fproxy) (Rec.get fproxy rec)
            fproxy = Proxy :: Proxy field
else
-- Recurse into nested records
instance rltvfConsRec :: 
    ( RLTVFiles row tail
    , IsSymbol field
    , Row.Cons field (Record irow) rtail row
    , RL.RowToList irow ilist
    , RLTVFiles irow ilist
    ) => RLTVFiles row (RL.Cons field (Record irow) tail) where
        rlFiles pfx _ rec = thisRec <> rlFiles pfx (Proxy :: Proxy tail) rec
          where
            fproxy = Proxy :: Proxy field
            irec = Rec.get fproxy rec
            thisRec = rlFiles (pfx <> reflectSymbol fproxy <> ".") (Proxy :: Proxy ilist) irec
else
-- Recurse into named type records
instance rltvfNamedTypeRec :: 
    ( RLTVFiles row tail
    , IsSymbol field
    , Row.Cons field (NamedType sym (Record irow)) rtail row
    , RL.RowToList irow ilist
    , RLTVFiles irow ilist
    ) => RLTVFiles row (RL.Cons field (RenamedType psym gsym (Record irow)) tail) where
        rlFiles pfx _ rec = thisRec <> rlFiles pfx (Proxy :: Proxy tail) rec
          where
            fproxy = Proxy :: Proxy field
            (RenamedType irec) = Rec.get fproxy rec
            thisRec = rlFiles (pfx <> reflectSymbol fproxy <> ".") (Proxy :: Proxy ilist) irec
else
-- Skip over everything else
instance rltvfConsOther :: 
    ( RLTVFiles row tail
    , IsSymbol field
    , Row.Cons field value rtail row
    ) => RLTVFiles row (RL.Cons field value tail) where
        rlFiles pfx _ rec = rlFiles pfx (Proxy :: Proxy tail) rec
-- End of list
instance rltvfNil :: RLTVFiles row RL.Nil where
    rlFiles _ _ _ = []
